/**
 * Per-boat wind samples for the wind-field overlay ("Mark Wind").
 *
 * The overlay's field was interpolated from the race marks alone. The boats measure wind too, and inside the
 * racing area they are far denser than the handful of marks. This module turns the live/replay row catalogs into
 * a handful of scattered wind samples the interpolator can consume alongside the mark readings.
 *
 * The boats' wind is read from the SGP fused solution (`Tws_sgp_kph` / `Twd_sgp_deg`) — it is the damped,
 * cross-checked wind, which is what belongs in a field estimate — with the canonical `Tws_kph` / `Twd_deg`
 * (aliased by the stream server to the less-damped team-model solution) as the fallback for data that carries no
 * SGP columns. See `BoatWindFields`: the caller passes the preference order, and it is resolved per source.
 *
 * Two things make a boat reading usable:
 *   - it must be MOVING (BSP > `BOAT_WIND_MIN_BSP_KPH`): a parked or drifting boat's apparent-wind solution is
 *     unreliable, the same reason `liveMarkWind.gradeMarks` only trusts a stationary mark's wind.
 *   - it must be AVERAGED: boat wind publishes at ~3-6 Hz and swings several degrees sample to sample, so a raw
 *     value would make both the field and the on-boat arrow jitter. A `BOAT_WIND_WINDOW_MS` trailing mean —
 *     CIRCULAR for TWD so the 0/360 wrap averages correctly (same math as liveMapFrameSnapshot) — is stable
 *     without lagging a real shift by more than a few seconds.
 *
 * Deliberately stateless: every call recomputes from the rows it is handed. There is no per-boat accumulator to
 * evict when the selection, the session or the columnar store changes.
 *
 * Channel names are supplied by the caller because they differ per surface: the live stream is ALWAYS kph/deg,
 * while the batch/replay catalogs follow `defaultChannelsStore`, which resolves to `_kts` for non-GP50 classes.
 * Speeds are normalized to km/h here — from whichever name actually resolved — so boat samples mix with mark wind
 * (always km/h) without the caller thinking about it.
 */

export interface BoatWindSample {
  /** Selection source id the rows were grouped under. */
  sourceId: number;
  /** Boat name, when the caller can resolve one (used to attach the arrow to the right boat). */
  name?: string;
  lat: number;
  lng: number;
  /** Windowed mean true wind speed, km/h. */
  tws: number;
  /** Windowed circular-mean true wind direction, degrees. */
  twd: number;
  /** Windowed mean boat speed, km/h — what the moving-boat gate was applied to. */
  bsp: number;
}

/**
 * Channel names to read each value from. A field may be a LIST, in which case the first name the rows actually
 * carry wins — that is how the wind channels ask for the SGP fused solution first and fall back to the canonical
 * channel on data (or a class) that has no SGP columns, instead of silently producing no boat wind at all.
 */
export interface BoatWindFields {
  lat: string | string[];
  lng: string | string[];
  tws: string | string[];
  twd: string | string[];
  bsp: string | string[];
}

/** BoatWindFields after each spec has been resolved against the rows in hand. */
export interface ResolvedBoatWindFields {
  lat: string;
  lng: string;
  tws: string;
  twd: string;
  bsp: string;
}

/** How many rows to probe when resolving a channel spec — enough to survive a sparse leading row or two. */
const FIELD_PROBE_ROWS = 5;

/**
 * First name in `spec` that these rows carry as a key (a SCHEMA probe, not a value check). Falls back to the
 * first name so the caller always has something to read, and a missing channel simply yields no samples.
 */
export function resolveFieldName(rows: any[], spec: string | string[]): string {
  if (typeof spec === "string") return spec;
  if (!Array.isArray(spec) || spec.length === 0) return "";
  const probe = Math.min(FIELD_PROBE_ROWS, rows.length);
  for (const name of spec) {
    for (let i = 0; i < probe; i++) {
      if (rows[i] && name in rows[i]) return name;
    }
  }
  return spec[0];
}

export function resolveFields(rows: any[], fields: BoatWindFields): ResolvedBoatWindFields {
  return {
    lat: resolveFieldName(rows, fields.lat),
    lng: resolveFieldName(rows, fields.lng),
    tws: resolveFieldName(rows, fields.tws),
    twd: resolveFieldName(rows, fields.twd),
    bsp: resolveFieldName(rows, fields.bsp),
  };
}

export interface BoatWindOptions {
  /** End of the trailing window (ms). Defaults to the newest row across all sources. */
  endMs?: number;
  windowMs?: number;
  minBspKph?: number;
  nameById?: (sourceId: number) => string | undefined;
}

/** Only boats above this mean speed (km/h) contribute wind. */
export const BOAT_WIND_MIN_BSP_KPH = 10;
/** Trailing window averaged into one sample per boat. */
export const BOAT_WIND_WINDOW_MS = 20_000;
/** Boats closer together than this are merged into one sample before interpolation. */
export const BOAT_WIND_DECLUSTER_M = 200;

const KTS_TO_KPH = 1.852;
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
const M_PER_DEG_LAT = 110540;
const M_PER_DEG_LNG = 111320;

/** km/h multiplier implied by a speed channel's name (`Bsp_kts` → 1.852, `Bsp_kph` → 1). */
export function speedScaleToKph(fieldName: string | undefined): number {
  return fieldName && /_kts$/i.test(fieldName) ? KTS_TO_KPH : 1;
}

function readFinite(value: unknown): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Row timestamp in ms, from either the numeric `timestamp` or the `Datetime` the row catalogs carry. */
export function rowMs(row: any): number {
  const ts = readFinite(row?.timestamp);
  if (ts !== undefined) return ts;
  const dt = row?.Datetime;
  if (dt instanceof Date) return dt.getTime();
  if (typeof dt === "string") {
    const n = new Date(dt).getTime();
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

/**
 * Merge boats that are close together into one sample each.
 *
 * The interpolator weights by 1/d², which is unbounded: a pack of boats within a boat-length of each other would
 * outvote a mark a kilometre away by orders of magnitude and flatten the field onto their reading. Binning them
 * onto a coarse grid first keeps the kernel untouched while making the boat population spatially fair. Wind is
 * merged as a VECTOR mean so the 0/360 wrap cannot produce a bogus average direction.
 */
export function declusterBoatWindSamples(
  samples: Array<{ lat: number; lng: number; tws: number; twd: number }> | null | undefined,
  binMeters: number = BOAT_WIND_DECLUSTER_M
): Array<{ lat: number; lng: number; tws: number; twd: number }> {
  if (!samples || samples.length === 0) return [];
  if (samples.length === 1 || !(binMeters > 0)) return samples.map((s) => ({ ...s }));

  const bins = new Map<string, { lat: number; lng: number; u: number; v: number; n: number }>();
  for (const s of samples) {
    if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng) || !Number.isFinite(s.tws) || !Number.isFinite(s.twd)) {
      continue;
    }
    const latStep = binMeters / M_PER_DEG_LAT;
    const lngStep = binMeters / Math.max(1, M_PER_DEG_LNG * Math.cos(s.lat * DEG2RAD));
    const key = `${Math.round(s.lat / latStep)}:${Math.round(s.lng / lngStep)}`;
    const u = s.tws * Math.sin(s.twd * DEG2RAD);
    const v = s.tws * Math.cos(s.twd * DEG2RAD);
    const bin = bins.get(key);
    if (bin) {
      bin.lat += s.lat;
      bin.lng += s.lng;
      bin.u += u;
      bin.v += v;
      bin.n++;
    } else {
      bins.set(key, { lat: s.lat, lng: s.lng, u, v, n: 1 });
    }
  }

  const out: Array<{ lat: number; lng: number; tws: number; twd: number }> = [];
  for (const bin of bins.values()) {
    const u = bin.u / bin.n;
    const v = bin.v / bin.n;
    const twd = Math.atan2(u, v) * RAD2DEG;
    out.push({
      lat: bin.lat / bin.n,
      lng: bin.lng / bin.n,
      tws: Math.hypot(u, v),
      twd: ((twd % 360) + 360) % 360,
    });
  }
  return out;
}

/**
 * Group a flat row array by `source_id` (Explore/Fleet track data comes flat).
 *
 * Single-source Explore data has no `source_id` column at all, so `fallbackSourceId` lets the caller name the one
 * boat those rows belong to — otherwise that map would silently never produce a boat wind sample.
 */
export function groupRowsBySourceId(
  rows: any[] | null | undefined,
  fallbackSourceId?: number
): Map<number, any[]> {
  const out = new Map<number, any[]>();
  if (!Array.isArray(rows)) return out;
  for (const row of rows) {
    let sid = Number(row?.source_id ?? row?.Source_id ?? row?.sourceId ?? row?.sourceID);
    if (!Number.isFinite(sid)) {
      if (!Number.isFinite(fallbackSourceId as number)) continue;
      sid = fallbackSourceId as number;
    }
    const bucket = out.get(sid);
    if (bucket) bucket.push(row);
    else out.set(sid, [row]);
  }
  return out;
}

/**
 * Rolling accumulator for "average these rows into one wind sample".
 *
 * Extracted so the single-window collector below and the multi-bin history collector
 * (windField/windSampleHistory) share ONE implementation of the averaging rules — circular mean for TWD, plain
 * mean for TWS/BSP, latest fix for position, moving-boat gate at the end. A plain struct plus free functions
 * rather than a class or closure: the history path allocates one of these per boat per time bin.
 */
export interface WindAcc {
  sinSum: number;
  cosSum: number;
  twdCount: number;
  twsSum: number;
  twsCount: number;
  bspSum: number;
  bspCount: number;
  lat?: number;
  lng?: number;
  posMs: number;
}

export function newWindAcc(): WindAcc {
  return { sinSum: 0, cosSum: 0, twdCount: 0, twsSum: 0, twsCount: 0, bspSum: 0, bspCount: 0, posMs: -Infinity };
}

/** Fold one row (already known to be in range, with its timestamp) into the accumulator. */
export function windAccAdd(
  acc: WindAcc,
  row: any,
  ms: number,
  f: ResolvedBoatWindFields,
  twsScale: number,
  bspScale: number
): void {
  const twd = readFinite(row[f.twd]);
  if (twd !== undefined) {
    acc.sinSum += Math.sin(twd * DEG2RAD);
    acc.cosSum += Math.cos(twd * DEG2RAD);
    acc.twdCount++;
  }
  const tws = readFinite(row[f.tws]);
  if (tws !== undefined) {
    acc.twsSum += tws * twsScale;
    acc.twsCount++;
  }
  const bsp = readFinite(row[f.bsp]);
  if (bsp !== undefined) {
    acc.bspSum += bsp * bspScale;
    acc.bspCount++;
  }
  // Position is the boat's LATEST fix in the window, not an average — the sample belongs where the boat is now,
  // while the wind it reports is the smoothed value it measured getting there.
  if (ms >= acc.posMs) {
    const rLat = readFinite(row[f.lat]);
    const rLng = readFinite(row[f.lng]);
    if (rLat !== undefined && rLng !== undefined) {
      acc.lat = rLat;
      acc.lng = rLng;
      acc.posMs = ms;
    }
  }
}

/** Averaged sample, or null when the window lacked wind/position or the boat was not moving. */
export function windAccFinish(
  acc: WindAcc,
  minBspKph: number
): { lat: number; lng: number; tws: number; twd: number; bsp: number } | null {
  if (!acc.twdCount || !acc.twsCount || !acc.bspCount) return null;
  if (acc.lat === undefined || acc.lng === undefined) return null;
  const bsp = acc.bspSum / acc.bspCount;
  if (!(bsp > minBspKph)) return null;
  const meanAngle = Math.atan2(acc.sinSum, acc.cosSum) * RAD2DEG;
  return {
    lat: acc.lat,
    lng: acc.lng,
    tws: acc.twsSum / acc.twsCount,
    twd: ((meanAngle % 360) + 360) % 360,
    bsp,
  };
}

/**
 * One averaged wind sample per moving boat.
 *
 * @param rowsBySource rows keyed by selection source id, ascending or not (the window filter does not assume
 *                     ordering, so the live snapshot and the batch track catalog both work).
 */
export function collectBoatWindSamples(
  rowsBySource: Map<number, any[]> | null | undefined,
  fields: BoatWindFields,
  opts: BoatWindOptions = {}
): BoatWindSample[] {
  if (!rowsBySource || rowsBySource.size === 0) return [];

  const windowMs = opts.windowMs ?? BOAT_WIND_WINDOW_MS;
  const minBspKph = opts.minBspKph ?? BOAT_WIND_MIN_BSP_KPH;

  let endMs = opts.endMs !== undefined && Number.isFinite(opts.endMs) ? (opts.endMs as number) : NaN;
  if (!Number.isFinite(endMs)) {
    for (const rows of rowsBySource.values()) {
      for (const row of rows) {
        const ms = rowMs(row);
        if (Number.isFinite(ms) && (!Number.isFinite(endMs) || ms > endMs)) endMs = ms;
      }
    }
  }
  if (!Number.isFinite(endMs)) return [];
  const startMs = endMs - windowMs;

  const out: BoatWindSample[] = [];
  for (const [sourceId, rows] of rowsBySource) {
    if (!Array.isArray(rows) || rows.length === 0) continue;

    // Resolved per source: two boats in the same selection can come from different schemas (e.g. one streamed
    // with SGP wind, one replayed from a file without it), and the km/h scale follows the name that won.
    const f = resolveFields(rows, fields);
    const twsScale = speedScaleToKph(f.tws);
    const bspScale = speedScaleToKph(f.bsp);

    const acc = newWindAcc();
    for (const row of rows) {
      const ms = rowMs(row);
      if (!Number.isFinite(ms) || ms < startMs || ms > endMs) continue;
      windAccAdd(acc, row, ms, f, twsScale, bspScale);
    }
    const avg = windAccFinish(acc, minBspKph);
    if (!avg) continue;

    out.push({ sourceId, name: opts.nameById?.(sourceId), ...avg });
  }

  return out;
}
